import { useState, useRef, useEffect, useCallback } from 'react';
import { MusicNoteBeamed, PlayBtn, Trash, PlusLg, Eye, EyeSlash, SkipBackward, SkipForward, Shuffle, VolumeUp, VolumeMute, PlayFill, PauseFill, HouseFill } from 'react-bootstrap-icons';
import './RadioPlayer.css';

const STREAM_URL = 'https://coderadio-admin-v2.freecodecamp.org/listen/coderadio/radio.mp3';
const NOW_PLAYING_URL = 'https://coderadio-admin-v2.freecodecamp.org/api/nowplaying/coderadio';
const DEFAULT_VIDEOS = ['a4tNJTZHzPg', 'am1VJP0RnmQ', 'ycIau2jkNSA', '5_4KRUx2iKY'];
const DEFAULT_BG_VIDEOS = ['liFjsSDj1eM', 'rjIbV_mv3i8', 'vTXfp4-cMKE', 'GGw3axV7IGs', 'hSwcHHPcw2o', '29S2rWCH1SU', 'IjseQOHq_mU'];
const LS_KEY = 'radio-youtube-playlist';
const BG_LS_KEY = 'radio-bg-playlist';

function parseYoutubeUrl(input) {
  const clean = input.trim();
  const m = clean.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/) || clean.match(/^([a-zA-Z0-9_-]{11})$/);
  return m ? m[1] : null;
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function RadioPlayer({ bgVideo, onBgVideoChange }) {
  const [source, setSource] = useState('youtube');
  const [ytSubSource, setYtSubSource] = useState('audio'); // 'audio' | 'background'
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(0.5);
  const [nowPlaying, setNowPlaying] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState(false);
  const [youtubePlaylist, setYoutubePlaylist] = useState(() => {
    try {
      const saved = localStorage.getItem(LS_KEY);
      return saved ? JSON.parse(saved) : [...DEFAULT_VIDEOS];
    } catch { return [...DEFAULT_VIDEOS]; }
  });
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showVideo, setShowVideo] = useState(false);
  const [inputUrl, setInputUrl] = useState('');
  const [shuffledOrder, setShuffledOrder] = useState(() => shuffle(youtubePlaylist));
  const [ytPlaying, setYtPlaying] = useState(false);
  const [ytVolume, setYtVolume] = useState(1);
  const [ytMuted, setYtMuted] = useState(false);

  // Background video state
  const [bgPlaylist, setBgPlaylist] = useState(() => {
    try {
      const saved = localStorage.getItem(BG_LS_KEY);
      return saved ? JSON.parse(saved) : [...DEFAULT_BG_VIDEOS];
    } catch { return [...DEFAULT_BG_VIDEOS]; }
  });
  const [bgShuffledOrder, setBgShuffledOrder] = useState(() => shuffle(DEFAULT_BG_VIDEOS));
  const [bgInputUrl, setBgInputUrl] = useState('');
  const [bgStartTime, setBgStartTime] = useState(10);

  const audioRef = useRef(null);
  const iframeRef = useRef(null);
  const stationName = 'freeCodeCamp Radio';

  useEffect(() => {
    localStorage.setItem(LS_KEY, JSON.stringify(youtubePlaylist));
  }, [youtubePlaylist]);

  useEffect(() => {
    localStorage.setItem(BG_LS_KEY, JSON.stringify(bgPlaylist));
  }, [bgPlaylist]);

  useEffect(() => {
    const audio = new Audio();
    audio.crossOrigin = 'anonymous';
    audio.src = STREAM_URL;
    audio.volume = volume;
    audio.loop = false;
    audio.preload = 'none';
    audioRef.current = audio;
    return () => {
      audio.pause();
      audio.src = '';
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  const fetchNowPlaying = useCallback(async () => {
    try {
      const res = await fetch(NOW_PLAYING_URL);
      const data = await res.json();
      const station = Array.isArray(data) ? data[0] : data;
      if (station?.now_playing?.song) {
        setNowPlaying({
          artist: station.now_playing.song.artist,
          title: station.now_playing.song.title,
          listeners: station.listeners?.current,
        });
      }
    } catch {}
  }, []);

  useEffect(() => {
    if (playing) {
      fetchNowPlaying();
      const interval = setInterval(fetchNowPlaying, 30000);
      return () => clearInterval(interval);
    }
  }, [playing, fetchNowPlaying]);

  const togglePlay = useCallback(() => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      audioRef.current.play()
        .then(() => {
          setPlaying(true);
          if (iframeRef.current) {
            iframeRef.current.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
          }
          setYtPlaying(false);
        })
        .catch(() => setError(true));
    }
  }, [playing]);

  const handleSourceChange = useCallback((newSource) => {
    if (newSource === source) return;
    if (newSource === 'youtube') {
      setShuffledOrder(shuffle(youtubePlaylist));
      setCurrentIndex(0);
    }
    setSource(newSource);
  }, [source, youtubePlaylist]);

  const toggleExpand = useCallback(() => setExpanded(p => !p), []);

  const nextVideo = useCallback(() => {
    setCurrentIndex(p => (p + 1 < shuffledOrder.length ? p + 1 : 0));
  }, [shuffledOrder.length]);

  const prevVideo = useCallback(() => {
    setCurrentIndex(p => (p > 0 ? p - 1 : shuffledOrder.length - 1));
  }, [shuffledOrder.length]);

  const playVideo = useCallback((index) => {
    setCurrentIndex(index);
  }, []);

  const reshufflePlaylist = useCallback(() => {
    setShuffledOrder(shuffle(youtubePlaylist));
    setCurrentIndex(0);
  }, [youtubePlaylist]);

  const addVideo = useCallback(() => {
    const id = parseYoutubeUrl(inputUrl);
    if (!id) return;
    setYoutubePlaylist(prev => [...prev, id]);
    setInputUrl('');
  }, [inputUrl]);

  const removeVideo = useCallback((index) => {
    setYoutubePlaylist(prev => {
      const next = prev.filter((_, i) => i !== index);
      return next.length ? next : DEFAULT_VIDEOS;
    });
  }, []);

  const handleInputKey = useCallback((e) => {
    if (e.key === 'Enter') addVideo();
  }, [addVideo]);

  const ytPlayingRef = useRef(ytPlaying);
  useEffect(() => { ytPlayingRef.current = ytPlaying; }, [ytPlaying]);

  const currentVideoId = shuffledOrder[currentIndex];
  const embedUrl = currentVideoId
    ? `https://www.youtube.com/embed/${currentVideoId}?autoplay=0&enablejsapi=1&rel=0&controls=1&modestbranding=1`
    : '';

  const sendYtCommand = useCallback((func, args = '') => {
    if (!iframeRef.current) return;
    iframeRef.current.contentWindow.postMessage(JSON.stringify({ event: 'command', func, args }), '*');
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (!e.data || typeof e.data !== 'object') return;
      if (e.source !== iframeRef.current?.contentWindow) return;
      if (e.data.event === 'onReady') {
        sendYtCommand(ytPlayingRef.current ? 'playVideo' : 'pauseVideo');
      } else if (e.data.event === 'onStateChange') {
        setYtPlaying(e.data.info === 1);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [sendYtCommand]);

  useEffect(() => {
    sendYtCommand(ytPlaying ? 'playVideo' : 'pauseVideo');
  }, [sendYtCommand, ytPlaying, currentVideoId]);

  useEffect(() => {
    if (playing && ytPlaying) {
      sendYtCommand('pauseVideo');
      setYtPlaying(false);
    }
  }, [playing, ytPlaying, sendYtCommand]);

  const toggleYtPlay = useCallback(() => {
    const starting = !ytPlaying;
    sendYtCommand(starting ? 'playVideo' : 'pauseVideo');
    if (starting) {
      sendYtCommand('unMute');
      sendYtCommand('setVolume', [ytVolume * 100]);
      if (audioRef.current) audioRef.current.pause();
      setPlaying(false);
      setYtMuted(false);
    }
    setYtPlaying(p => !p);
  }, [sendYtCommand, ytPlaying, ytVolume]);

  const handleYtVolume = useCallback((e) => {
    const val = parseFloat(e.target.value);
    setYtVolume(val);
    if (iframeRef.current) {
      iframeRef.current.contentWindow.postMessage(JSON.stringify({event:'command',func:'setVolume',args:[val * 100]}), '*');
    }
    if (val > 0 && ytMuted) setYtMuted(false);
  }, [ytMuted]);

  const toggleYtMute = useCallback(() => {
    if (!iframeRef.current) return;
    if (ytMuted) {
      iframeRef.current.contentWindow.postMessage('{"event":"command","func":"unMute","args":""}', '*');
      iframeRef.current.contentWindow.postMessage(JSON.stringify({event:'command',func:'setVolume',args:[ytVolume * 100]}), '*');
    } else {
      iframeRef.current.contentWindow.postMessage('{"event":"command","func":"mute","args":""}', '*');
    }
    setYtMuted(p => !p);
  }, [ytMuted, ytVolume]);

  const isPlaying = source === 'radio' ? playing : ytPlaying;

  // ─── Background video helpers ────────────────────────────
  const currentBgId = bgShuffledOrder[bgVideo?.bgIndex ?? 0];

  const playBgVideo = useCallback((index) => {
    const id = bgShuffledOrder[index];
    if (!id) return;
    onBgVideoChange?.(prev => ({
      ...prev,
      videoId: id,
      startTime: bgStartTime,
      isPlaying: true,
      isMuted: true,
      volume: 0,
      bgIndex: index,
    }));
  }, [bgShuffledOrder, bgStartTime, onBgVideoChange]);

  const toggleBgPlay = useCallback(() => {
    if (!onBgVideoChange) return;
    if (bgVideo?.isPlaying) {
      onBgVideoChange(prev => ({ ...prev, isPlaying: false }));
    } else {
      onBgVideoChange(prev => ({
        ...prev,
        videoId: prev.videoId || currentBgId || DEFAULT_BG_VIDEOS[0],
        startTime: bgStartTime,
        isPlaying: true,
        isMuted: true,
        volume: 0,
      }));
    }
  }, [bgVideo, bgStartTime, currentBgId, onBgVideoChange]);

  const nextBgVideo = useCallback(() => {
    const len = bgShuffledOrder.length;
    if (!len) return;
    const cur = bgVideo?.bgIndex ?? 0;
    const next = (cur + 1) % len;
    playBgVideo(next);
  }, [bgShuffledOrder.length, bgVideo, playBgVideo]);

  const prevBgVideo = useCallback(() => {
    const len = bgShuffledOrder.length;
    if (!len) return;
    const cur = bgVideo?.bgIndex ?? 0;
    const next = (cur - 1 + len) % len;
    playBgVideo(next);
  }, [bgShuffledOrder.length, bgVideo, playBgVideo]);

  const reshuffleBgPlaylist = useCallback(() => {
    const order = shuffle(bgPlaylist);
    setBgShuffledOrder(order);
    if (order.length && onBgVideoChange) {
      onBgVideoChange(prev => ({
        ...prev,
        videoId: order[0],
        startTime: bgStartTime,
        isPlaying: true,
        isMuted: true,
        volume: 0,
        bgIndex: 0,
      }));
    }
  }, [bgPlaylist, bgStartTime, onBgVideoChange]);

  const addBgVideo = useCallback(() => {
    const id = parseYoutubeUrl(bgInputUrl);
    if (!id) return;
    setBgPlaylist(prev => {
      if (prev.includes(id)) return prev;
      return [...prev, id];
    });
    setBgInputUrl('');
  }, [bgInputUrl]);

  const removeBgVideo = useCallback((index) => {
    setBgPlaylist(prev => {
      const next = prev.filter((_, i) => i !== index);
      return next.length ? next : DEFAULT_BG_VIDEOS;
    });
  }, []);

  const handleBgInputKey = useCallback((e) => {
    if (e.key === 'Enter') addBgVideo();
  }, [addBgVideo]);

  const handleBgStartTime = useCallback((e) => {
    const val = parseInt(e.target.value) || 0;
    setBgStartTime(val);
    if (bgVideo?.isPlaying) {
      onBgVideoChange?.(prev => ({ ...prev, startTime: val }));
    }
  }, [bgVideo, onBgVideoChange]);

  return (
    <div className={`radio-player ${expanded ? 'expanded' : ''} ${isPlaying || bgVideo?.isPlaying ? 'is-playing' : ''}`}>
      <button className="radio-toggle" onClick={toggleExpand} title={source === 'radio' ? stationName : 'YouTube'}>
        <span className="radio-icon">{source === 'radio' ? <MusicNoteBeamed size={18} /> : <PlayBtn size={18} />}</span>
      </button>

      <div className={`radio-body ${!expanded ? 'radio-body-collapsed' : ''} ${source === 'youtube' ? 'radio-body-youtube' : ''}`}>
          <div className="radio-source-pills">
            <button className={`radio-source-pill ${source === 'radio' ? 'active' : ''}`} onClick={() => handleSourceChange('radio')}>
              <MusicNoteBeamed size={14} style={{ marginRight: 4 }} /> Radio
            </button>
            <button className={`radio-source-pill ${source === 'youtube' ? 'active' : ''}`} onClick={() => handleSourceChange('youtube')}>
              <PlayBtn size={14} style={{ marginRight: 4 }} /> YouTube
            </button>
          </div>

          <div className={`radio-yt-sub-pills ${source !== 'youtube' ? 'radio-yt-sub-pills-hidden' : ''}`}>
            <button
              className={`radio-yt-sub-pill ${ytSubSource === 'audio' ? 'active' : ''}`}
              onClick={() => setYtSubSource('audio')}
            >
              <MusicNoteBeamed size={12} style={{ marginRight: 3 }} /> Audio
            </button>
            <button
              className={`radio-yt-sub-pill ${ytSubSource === 'background' ? 'active' : ''}`}
              onClick={() => setYtSubSource('background')}
            >
              <HouseFill size={12} style={{ marginRight: 3 }} /> Background
            </button>
          </div>

          <div className={`radio-youtube-embed ${source !== 'youtube' ? 'embed-hidden' : ''} ${showVideo ? 'video-visible' : 'video-hidden'} ${ytSubSource !== 'audio' ? 'embed-hidden' : ''}`}>
            {embedUrl && (
              <iframe ref={iframeRef} src={embedUrl} width="100%" height="200" allow="autoplay; encrypted-media; fullscreen" title="YouTube player" onLoad={() => sendYtCommand(ytPlayingRef.current ? 'playVideo' : 'pauseVideo')} />
            )}
          </div>

          {source === 'radio' ? (
            <>
              <div className="radio-header">
                <span className="radio-station">{stationName}</span>
                {nowPlaying?.listeners && <span className="radio-listeners">{nowPlaying.listeners} oyentes</span>}
              </div>
              <div className="radio-now">
                {nowPlaying ? (
                  <span className="radio-song">{nowPlaying.artist} — {nowPlaying.title}</span>
                ) : (
                  <span className="radio-song muted">Cargando...</span>
                )}
              </div>
              <div className="radio-controls">
                <button className="radio-play-btn" onClick={togglePlay}>{playing ? <PauseFill size={14} /> : <PlayFill size={14} />}</button>
                <div className="radio-volume-wrap">
                  <VolumeUp size={14} style={{ color: 'rgba(255,255,255,0.2)', flexShrink: 0 }} />
                  <input type="range" className="radio-volume" min="0" max="1" step="0.05" value={volume} onChange={e => setVolume(parseFloat(e.target.value))} />
                </div>
              </div>
              {error && <div className="radio-error">No se pudo iniciar el stream. Hacé clic en el botón para intentar de nuevo.</div>}
            </>
          ) : (
            <div className="radio-youtube">
              {ytSubSource === 'audio' ? (
                <>
                  <div className="radio-youtube-toggle-video">
                    <button className="radio-yt-toggle-btn" onClick={() => setShowVideo(p => !p)}>
                      {showVideo ? <EyeSlash size={14} style={{ marginRight: 4 }} /> : <Eye size={14} style={{ marginRight: 4 }} />}
                      {showVideo ? 'Ocultar video' : 'Mostrar video'}
                    </button>
                  </div>

                  <div className="radio-yt-nav">
                    <button className="radio-yt-nav-btn" onClick={prevVideo}><SkipBackward size={16} /></button>
                    <button className="radio-yt-nav-btn" onClick={reshufflePlaylist} title="Aleatorio"><Shuffle size={14} /></button>
                    <span className="radio-yt-track">{currentIndex + 1} / {shuffledOrder.length}</span>
                    <button className="radio-yt-nav-btn" onClick={nextVideo}><SkipForward size={16} /></button>
                  </div>

                  <div className="radio-yt-controls">
                    <button className="radio-yt-play-btn" onClick={toggleYtPlay}>{ytPlaying ? <PauseFill size={14} /> : <PlayFill size={14} />}</button>
                    <div className="radio-yt-volume-wrap">
                      <button className="radio-yt-mute-btn" onClick={toggleYtMute}>{ytMuted ? <VolumeMute size={16} /> : <VolumeUp size={16} />}</button>
                      <input type="range" className="radio-yt-volume" min="0" max="1" step="0.05" value={ytVolume} onChange={handleYtVolume} />
                    </div>
                  </div>

                  <div className="radio-yt-playlist">
                    {shuffledOrder.map((id, i) => (
                      <div key={`${id}-${i}`} className={`radio-yt-item ${i === currentIndex ? 'playing' : ''}`} onClick={() => playVideo(i)}>
                        <img src={`https://img.youtube.com/vi/${id}/default.jpg`} alt="" className="radio-yt-thumb" width="120" height="90" loading="lazy" />
                        <span className="radio-yt-id">{id}</span>
                        <button className="radio-yt-remove" onClick={e => { e.stopPropagation(); removeVideo(i); }}><Trash size={12} /></button>
                      </div>
                    ))}
                  </div>

                  <div className="radio-yt-add">
                    <input
                      type="text"
                      className="radio-yt-input"
                      placeholder="URL de YouTube..."
                      value={inputUrl}
                      onChange={e => setInputUrl(e.target.value)}
                      onKeyDown={handleInputKey}
                    />
                    <button className="radio-yt-add-btn" onClick={addVideo}><PlusLg size={14} /></button>
                  </div>
                </>
              ) : (
                <>
                  <div className="radio-bg-status">
                    <span className={`radio-bg-dot ${bgVideo?.isPlaying ? 'playing' : ''}`}></span>
                    <span className="radio-bg-label">
                      {bgVideo?.isPlaying ? `Fondo: ${bgVideo.videoId}` : 'Fondo detenido'}
                    </span>
                  </div>

                  <div className="radio-yt-nav">
                    <button className="radio-yt-nav-btn" onClick={prevBgVideo}><SkipBackward size={16} /></button>
                    <button className="radio-yt-nav-btn" onClick={reshuffleBgPlaylist} title="Aleatorio"><Shuffle size={14} /></button>
                    <span className="radio-yt-track">{bgShuffledOrder.length ? ((bgVideo?.bgIndex ?? 0) + 1) : 0} / {bgShuffledOrder.length}</span>
                    <button className="radio-yt-nav-btn" onClick={nextBgVideo}><SkipForward size={16} /></button>
                  </div>

                  <div className="radio-yt-controls">
                    <button className="radio-yt-play-btn" onClick={toggleBgPlay}>{bgVideo?.isPlaying ? <PauseFill size={14} /> : <PlayFill size={14} />}</button>
                    <div className="radio-bg-start-wrap">
                      <span className="radio-bg-start-label">Inicio</span>
                      <input
                        type="number"
                        className="radio-bg-start-input"
                        min="0"
                        value={bgStartTime}
                        onChange={handleBgStartTime}
                        title="Segundos de inicio"
                      />
                      <span className="radio-bg-start-unit">s</span>
                    </div>
                  </div>

                  <div className="radio-yt-playlist">
                    {bgShuffledOrder.map((id, i) => (
                      <div key={`${id}-${i}`} className={`radio-yt-item ${bgVideo?.videoId === id ? 'playing' : ''}`} onClick={() => playBgVideo(i)}>
                        <img src={`https://img.youtube.com/vi/${id}/default.jpg`} alt="" className="radio-yt-thumb" width="120" height="90" loading="lazy" />
                        <span className="radio-yt-id">{id}</span>
                        <button className="radio-yt-remove" onClick={e => { e.stopPropagation(); removeBgVideo(i); }}><Trash size={12} /></button>
                      </div>
                    ))}
                  </div>

                  <div className="radio-yt-add">
                    <input
                      type="text"
                      className="radio-yt-input"
                      placeholder="URL de fondo..."
                      value={bgInputUrl}
                      onChange={e => setBgInputUrl(e.target.value)}
                      onKeyDown={handleBgInputKey}
                    />
                    <button className="radio-yt-add-btn" onClick={addBgVideo}><PlusLg size={14} /></button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
    </div>
  );
}
